'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Eye } from 'lucide-react';
import type { EventData } from '@/lib/fetch-event-data';
import { MetaPixelScript } from '@/components/booking/MetaPixelScript';
import { BookingPoweredBy } from '@/components/booking/BookingPoweredBy';
import { GtmScript } from '@/components/GtmScript';
import { isSmartFlow } from '@shared/booking/form-variant';
import { BookingFormSkeleton } from '@/components/booking/BookingFormSkeleton';

// Two wrappers over the same chunk so the loading skeleton matches the event's
// form variant (the dynamic `loading` callback can't see props). SmartFlow forms
// render their logo as a page header here, so the SmartFlow skeleton hides it.
const widgetImport = () => import('@/components/booking/BookingWidget').then(m => ({ default: m.BookingWidget }));
const BookingWidgetSmartFlow = dynamic(widgetImport, { ssr: false, loading: () => <BookingFormSkeleton variant="smartflow" hideLogo /> });
const BookingWidgetClassic = dynamic(widgetImport, { ssr: false, loading: () => <BookingFormSkeleton variant="classic" /> });

interface Props {
  eventData: EventData;
  isPreviewMode: boolean;
  /** Suppresses the amber preview banner without changing preview semantics. Used when
   * the page is loaded inside the salespads-editor mobile-preview iframe — the
   * surrounding editor chrome already makes the preview context obvious. */
  hideBanner?: boolean;
}

export function BookingPageClient({ eventData, isPreviewMode, hideBanner = false }: Props) {
  const showBanner = isPreviewMode && !hideBanner;
  const isDarkMode = eventData.event.widget_theme === 'dark';
  // SmartFlow (EventBuilder 2.0) renders full-page: white (or dark) background,
  // form vertically centred, and a single page-level "Powered by SalesCal"
  // footer instead of the boxed card layout used by slide/stepped forms.
  const smartFlow = isSmartFlow(eventData.event.form_style);
  // SmartFlow logo is rendered as a top-of-page header (not inside the card).
  const showLogo = eventData.event.show_logo ?? true;
  const logoUrl = eventData.event.custom_logo_url || eventData.profile?.company_logo_url || null;

  // Set body background to prevent white flash
  useEffect(() => {
    document.body.style.backgroundColor = isDarkMode
      ? '#030712'
      : smartFlow
        ? '#ffffff'
        : '#f9fafb';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [isDarkMode, smartFlow]);

  const banner = showBanner && (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-amber-950 py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2 z-50">
      <Eye className="h-4 w-4" />
      Preview Mode - This event is not yet live. Bookings will not be saved.
    </div>
  );
  const trackingScripts = (
    <>
      {eventData.gtmContainerId && <GtmScript containerId={eventData.gtmContainerId} />}
      {eventData.metaPixelIds.length > 0 && (
        <MetaPixelScript pixelIds={eventData.metaPixelIds} pageViewEventName={eventData.metaPixelEventNames?.page_view} />
      )}
    </>
  );
  const Widget = smartFlow ? BookingWidgetSmartFlow : BookingWidgetClassic;
  const widget = (
    <Widget
      event={eventData.event}
      fields={eventData.fields}
      ruleSets={eventData.ruleSets}
      profile={eventData.profile}
      userEmail={eventData.profile.email}
      isPreviewMode={isPreviewMode}
      metaPixelIds={eventData.metaPixelIds}
      teamId={eventData.teamId}
      metaPixelEventNames={eventData.metaPixelEventNames}
      confirmationIncludeCancelReschedule={eventData.confirmationIncludeCancelReschedule}
      gtmContainerId={eventData.gtmContainerId}
      eventSlug={eventData.event.slug}
      bookingPaused={eventData.bookingPaused}
      hidePoweredBy={smartFlow}
      hideLogo={smartFlow}
      progressBarMode="viewport"
    />
  );
  const logoHeader = showLogo && logoUrl && (
    <div className="flex justify-center px-6 pt-8 pb-2">
      <img src={logoUrl} alt="Company logo" className="h-9 max-w-[200px] object-contain" />
    </div>
  );

  // SmartFlow: flat full-page layout, logo at top, form centred, page-level footer.
  if (smartFlow) {
    return (
      <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
        {banner}
        {trackingScripts}
        {logoHeader}
        <div className={`flex flex-1 justify-center px-4 w-full max-md:items-start max-md:pt-10 max-md:pb-8 md:items-center md:py-8 ${showBanner ? 'pt-14' : ''}`}>
          {widget}
        </div>
        <BookingPoweredBy isDarkMode={isDarkMode} />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 px-4 ${
        isDarkMode
          ? 'bg-gradient-to-br from-gray-900 to-gray-950'
          : 'bg-gradient-to-br from-gray-50 to-gray-100'
      }`}
    >
      {banner}
      {trackingScripts}
      <div className={showBanner ? 'pt-10' : ''}>
        {widget}
      </div>
    </div>
  );
}
